import React from 'react';
import { Network, Activity, Shield, Eye, AlertCircle, Database, Lock, Server, Zap } from 'lucide-react';
import { useLanguage } from '../../components/LanguageContext';

export function NetworkMonitoring() {
  const { language } = useLanguage();
  
  const content = {
    hero: {
      title: {
        fr: 'Surveillance Réseau 24/7',
        en: '24/7 Network Monitoring'
      },
      subtitle: {
        fr: 'Détection et réponse continues aux menaces réseau',
        en: 'Continuous network threat detection and response'
      },
      description: {
        fr: `Notre service de surveillance réseau 24/7 combine une technologie de pointe avec une expertise 
             humaine pour protéger votre infrastructure réseau contre les menaces émergentes. Nous utilisons 
             des algorithmes avancés de détection d'anomalies et d'apprentissage automatique pour identifier 
             les comportements suspects avant qu'ils ne deviennent des incidents de sécurité critiques.`,
        en: `Our 24/7 network monitoring service combines cutting-edge technology with human expertise to 
             protect your network infrastructure against emerging threats. We use advanced anomaly detection 
             and machine learning algorithms to identify suspicious behavior before it becomes a critical 
             security incident.`
      }
    },
    overview: {
      title: {
        fr: 'Protection Réseau Complète',
        en: 'Comprehensive Network Protection'
      },
      content: {
        fr: `Notre approche de la surveillance réseau repose sur trois piliers fondamentaux : la visibilité 
             totale, l'analyse en temps réel et la réponse automatisée. Nous déployons des capteurs 
             sophistiqués à travers votre réseau pour collecter et analyser le trafic en temps réel, 
             permettant une détection précoce des menaces et une réponse immédiate.`,
        en: `Our network monitoring approach is built on three fundamental pillars: complete visibility, 
             real-time analysis, and automated response. We deploy sophisticated sensors throughout your 
             network to collect and analyze traffic in real-time, enabling early threat detection and 
             immediate response.`
      }
    },
    features: [
      {
        icon: <Network className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Visibilité Complète',
          en: 'Complete Visibility'
        },
        description: {
          fr: 'Surveillance de tout le trafic réseau avec analyse en temps réel',
          en: 'Monitor all network traffic with real-time analysis'
        },
        details: {
          fr: `Notre solution offre une visibilité complète sur l'ensemble de votre trafic réseau, 
               y compris le trafic chiffré, grâce à des techniques avancées d'inspection des paquets 
               et d'analyse comportementale. Nous surveillons les communications nord-sud et est-ouest 
               pour une protection maximale.`,
          en: `Our solution provides complete visibility into all your network traffic, including 
               encrypted traffic, through advanced packet inspection and behavioral analysis techniques. 
               We monitor both north-south and east-west communications for maximum protection.`
        }
      },
      {
        icon: <Activity className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Détection d\'Anomalies',
          en: 'Anomaly Detection'
        },
        description: {
          fr: 'Identification immédiate des comportements suspects',
          en: 'Immediate identification of suspicious behavior'
        },
        details: {
          fr: `Nos algorithmes d'apprentissage automatique établissent des profils de comportement 
               normal pour votre réseau et détectent instantanément toute déviation. Cette approche 
               permet d'identifier les menaces zero-day et les attaques sophistiquées qui échappent 
               aux systèmes traditionnels.`,
          en: `Our machine learning algorithms establish normal behavior profiles for your network 
               and instantly detect any deviation. This approach enables identification of zero-day 
               threats and sophisticated attacks that evade traditional systems.`
        }
      },
      {
        icon: <Shield className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Protection Proactive',
          en: 'Proactive Protection'
        },
        description: {
          fr: 'Mise en place de mesures préventives basées sur l\'analyse',
          en: 'Implementation of preventive measures based on analysis'
        },
        details: {
          fr: `Notre système ne se contente pas de détecter les menaces, il prend des mesures 
               proactives pour les bloquer. Nous utilisons l'intelligence artificielle pour prédire 
               et prévenir les attaques avant qu'elles n'atteignent vos systèmes critiques.`,
          en: `Our system doesn't just detect threats, it takes proactive measures to block them. 
               We use artificial intelligence to predict and prevent attacks before they reach your 
               critical systems.`
        }
      }
    ],
    capabilities: {
      title: {
        fr: 'Capacités de Surveillance',
        en: 'Monitoring Capabilities'
      },
      description: {
        fr: `Notre plateforme de surveillance réseau intègre des technologies de pointe et des 
             processus éprouvés pour offrir une protection complète. Chaque composant est optimisé 
             pour une détection rapide et une réponse efficace aux menaces.`,
        en: `Our network monitoring platform integrates cutting-edge technologies and proven processes 
             to provide comprehensive protection. Each component is optimized for rapid detection and 
             efficient threat response.`
      },
      items: [
        {
          icon: <Database className="w-6 h-6 text-blue-500" />,
          title: {
            fr: 'Analyse du Trafic',
            en: 'Traffic Analysis'
          },
          description: {
            fr: 'Inspection approfondie des paquets et analyse comportementale',
            en: 'Deep packet inspection and behavioral analysis'
          },
          details: {
            fr: `Notre système analyse chaque paquet de données traversant votre réseau, utilisant 
                 des techniques avancées d'inspection pour identifier les menaces cachées et les 
                 comportements malveillants.`,
            en: `Our system analyzes every data packet crossing your network, using advanced inspection 
                 techniques to identify hidden threats and malicious behaviors.`
          }
        },
        {
          icon: <Lock className="w-6 h-6 text-blue-500" />,
          title: {
            fr: 'Détection des Menaces',
            en: 'Threat Detection'
          },
          description: {
            fr: 'Identification des malwares, intrusions et activités malveillantes',
            en: 'Identification of malware, intrusions, and malicious activities'
          },
          details: {
            fr: `Combinaison de signatures connues et d'analyse comportementale pour détecter un large 
                 éventail de menaces, des attaques simples aux APT sophistiquées.`,
            en: `Combination of known signatures and behavioral analysis to detect a wide range of 
                 threats, from simple attacks to sophisticated APTs.`
          }
        },
        {
          icon: <Server className="w-6 h-6 text-blue-500" />,
          title: {
            fr: 'Réponse aux Incidents',
            en: 'Incident Response'
          },
          description: {
            fr: 'Protocoles de réponse automatisés et intervention rapide',
            en: 'Automated response protocols and rapid intervention'
          },
          details: {
            fr: `Réponse automatisée aux menaces détectées, avec des workflows personnalisables pour 
                 différents types d'incidents et niveaux de gravité.`,
            en: `Automated response to detected threats, with customizable workflows for different 
                 types of incidents and severity levels.`
          }
        }
      ]
    },
    methodology: {
      title: {
        fr: 'Notre Méthodologie',
        en: 'Our Methodology'
      },
      steps: [
        {
          icon: <Eye className="w-6 h-6 text-blue-500" />,
          title: {
            fr: 'Collecte de Données',
            en: 'Data Collection'
          },
          description: {
            fr: `Déploiement stratégique de capteurs et collecte continue de données réseau pour une 
                 visibilité maximale.`,
            en: `Strategic sensor deployment and continuous network data collection for maximum 
                 visibility.`
          }
        },
        {
          icon: <Activity className="w-6 h-6 text-blue-500" />,
          title: {
            fr: 'Analyse en Temps Réel',
            en: 'Real-time Analysis'
          },
          description: {
            fr: `Traitement instantané des données collectées avec des algorithmes avancés pour 
                 détecter les anomalies.`,
            en: `Instant processing of collected data with advanced algorithms to detect anomalies.`
          }
        },
        {
          icon: <AlertCircle className="w-6 h-6 text-blue-500" />,
          title: {
            fr: 'Détection des Menaces',
            en: 'Threat Detection'
          },
          description: {
            fr: `Identification précise des menaces grâce à l'intelligence artificielle et l'analyse 
                 comportementale.`,
            en: `Precise threat identification through artificial intelligence and behavioral analysis.`
          }
        },
        {
          icon: <Zap className="w-6 h-6 text-blue-500" />,
          title: {
            fr: 'Réponse Automatisée',
            en: 'Automated Response'
          },
          description: {
            fr: `Mise en œuvre immédiate de contre-mesures pour neutraliser les menaces détectées.`,
            en: `Immediate implementation of countermeasures to neutralize detected threats.`
          }
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Hero Section */}
      <div className="hero-pattern py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <Network className="w-16 h-16 text-blue-500 mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {content.hero.title[language]}
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              {content.hero.subtitle[language]}
            </p>
            <p className="text-lg text-gray-400 leading-relaxed">
              {content.hero.description[language]}
            </p>
          </div>
        </div>
      </div>

      {/* Live Dashboard Preview */}
      <div className="container mx-auto px-6 py-12">
        <div className="bg-gray-800/50 rounded-lg overflow-hidden shadow-2xl">
          <img 
            src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&q=80" 
            alt="Network Monitoring Dashboard"
            className="w-full h-auto"
          />
          <div className="p-6">
            <h3 className="text-xl font-bold text-white mb-2">
              {language === 'fr' ? 'Tableau de Bord en Direct' : 'Live Dashboard'}
            </h3>
            <p className="text-gray-300">
              {language === 'fr' 
                ? 'Surveillance en temps réel de votre infrastructure réseau avec détection instantanée des menaces.'
                : 'Real-time monitoring of your network infrastructure with instant threat detection.'}
            </p>
          </div>
        </div>
      </div>

      {/* Overview Section */}
      <div className="container mx-auto px-6 py-20">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-6">
            {content.overview.title[language]}
          </h2>
          <p className="text-lg text-gray-300 leading-relaxed text-center">
            {content.overview.content[language]}
          </p>
        </div>
      </div>

      {/* Features Grid with Detailed Explanations */}
      <div className="container mx-auto px-6 py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {content.features.map((feature, index) => (
            <div key={index} className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-lg border border-gray-700/50 hover:border-blue-500/50 transition-colors duration-300">
              <div className="mb-4">{feature.icon}</div>
              <h3 className="text-xl font-bold text-white mb-3">
                {feature.title[language]}
              </h3>
              <p className="text-gray-300 mb-4">
                {feature.description[language]}
              </p>
              <p className="text-gray-400 text-sm leading-relaxed">
                {feature.details[language]}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Methodology Section */}
      <div className="bg-gray-800/50 py-20">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            {content.methodology.title[language]}
          </h2>
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              <div className="absolute left-8 inset-y-0 w-0.5 bg-blue-500/30"></div>
              <div className="space-y-12">
                {content.methodology.steps.map((step, index) => (
                  <div key={index} className="relative flex items-center gap-8">
                    <div className="absolute left-8 w-3 h-3 bg-blue-500 rounded-full transform -translate-x-1.5"></div>
                    <div className="pl-20">
                      <h3 className="text-xl font-bold text-white mb-2">
                        {step.title[language]}
                      </h3>
                      <p className="text-gray-300">
                        {step.description[language]}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Capabilities Section with In-depth Details */}
      <div className="container mx-auto px-6 py-20">
        <h2 className="text-3xl font-bold text-white text-center mb-6">
          {content.capabilities.title[language]}
        </h2>
        <p className="text-lg text-gray-300 text-center max-w-4xl mx-auto mb-12">
          {content.capabilities.description[language]}
        </p>
        <div className="max-w-4xl mx-auto space-y-8">
          {content.capabilities.items.map((item, index) => (
            <div key={index} className="flex items-start space-x-4 bg-gray-800/50 p-6 rounded-lg">
              <div className="flex-shrink-0 bg-blue-500/10 p-3 rounded-lg">
                {item.icon}
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">
                  {item.title[language]}
                </h3>
                <p className="text-gray-300 mb-3">
                  {item.description[language]}
                </p>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {item.details[language]}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="container mx-auto px-6 py-20">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-12 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&q=80')] opacity-10 bg-cover bg-center"></div>
          <div className="relative z-10">
            <h2 className="text-3xl font-bold text-white mb-4">
              {language === 'fr' ? 'Prêt à Sécuriser Votre Réseau ?' : 'Ready to Secure Your Network?'}
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              {language === 'fr' 
                ? 'Découvrez comment notre surveillance réseau peut protéger votre entreprise' 
                : 'Discover how our network monitoring can protect your business'}
            </p>
            <button className="bg-white text-blue-600 px-8 py-3 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105">
              {language === 'fr' ? 'Commencer Maintenant' : 'Get Started Now'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}